import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { ArrowLeft, CheckCircle, XCircle, Upload, Download, FileSpreadsheet, AlertTriangle, Loader2, X } from 'lucide-react';

const ManageMembers = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchMembers();
  }, [filter]);

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = filter === 'all' 
        ? '/api/admin/members'
        : `/api/admin/members?status=${filter}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) setMembers(await response.json());
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateMemberStatus = async (memberId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`/api/admin/members/${memberId}/status?status=${status}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: `Member ${status} successfully!` });
      fetchMembers();
    } catch (error) {
      toast({ title: 'Error updating member status', variant: 'destructive' });
    }
  };

  const handleBulkImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await fetch('/api/admin/members/bulk-import', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setImportResult(data);
        toast({ title: data.message });
        fetchMembers();
      } else {
        toast({ title: data.detail || 'Import failed', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Import error', variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem('admin_token');
    const res = await fetch('/api/admin/members/import-template', { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ndp_member_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">Manage Members</h1>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button className="bg-party-green hover:bg-green-700" onClick={() => { setShowImport(!showImport); setImportResult(null); setImportFile(null); }} data-testid="bulk-import-toggle">
                <Upload className="h-4 w-4 mr-2" />
                Bulk Import
              </Button>
              <div className="flex space-x-1">
                {['all', 'pending', 'approved', 'rejected'].map(f => (
                  <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'} onClick={() => setFilter(f)} className={filter === f ? 'bg-party-red hover:bg-red-700' : ''}>
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Bulk Import Panel */}
        {showImport && (
          <Card className="mb-6 border-party-green/30 bg-green-50/30" data-testid="bulk-import-panel">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileSpreadsheet className="h-5 w-5 text-party-green" /> Bulk Member Import
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowImport(false)}><X className="h-4 w-4" /></Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3 items-start">
                <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn">
                  <Download className="h-4 w-4 mr-2" /> Download CSV Template
                </Button>
                <p className="text-xs text-gray-500 pt-1">Supports <strong>.csv</strong> and <strong>.xlsx</strong> files. Required columns: full_name, father_name, dob, nid, mobile, present_address, profession, education</p>
              </div>

              <div
                className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-party-green transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setImportFile(f); }}
                data-testid="import-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  data-testid="import-file-input"
                />
                {importFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileSpreadsheet className="h-8 w-8 text-party-green" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{importFile.name}</p>
                      <p className="text-xs text-gray-500">{(importFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setImportFile(null); }}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Click or drag & drop your CSV/Excel file here</p>
                  </>
                )}
              </div>

              <Button
                className="bg-party-green hover:bg-green-700 w-full"
                disabled={!importFile || importing}
                onClick={handleBulkImport}
                data-testid="start-import-btn"
              >
                {importing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing...</> : <><Upload className="h-4 w-4 mr-2" /> Import Members</>}
              </Button>

              {/* Import Results */}
              {importResult && (
                <div className="bg-white rounded-xl p-4 border space-y-3" data-testid="import-results">
                  <div className="flex gap-4 text-sm">
                    <span className="flex items-center gap-1 text-green-600 font-bold"><CheckCircle className="h-4 w-4" /> {importResult.imported} imported</span>
                    {importResult.skipped > 0 && <span className="flex items-center gap-1 text-amber-600 font-bold"><AlertTriangle className="h-4 w-4" /> {importResult.skipped} skipped</span>}
                    <span className="text-gray-400">of {importResult.total_rows} rows</span>
                  </div>
                  {importResult.errors?.length > 0 && (
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {importResult.errors.map((err, i) => (
                        <div key={i} className="text-xs bg-red-50 text-red-700 px-3 py-1.5 rounded flex gap-2">
                          <span className="font-bold shrink-0">Row {err.row}:</span>
                          <span className="truncate">{err.name} — {err.error}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-party-red mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {members.map((member) => (
              <Card key={member._id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>{member.full_name}</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">{member.member_number || 'No member no.'} · {member.district || 'No district'} · {member.email || 'No email'}</p>
                      <div className="mt-2 flex flex-wrap gap-1.5 text-xs" data-testid={`member-tags-${member._id}`}>
                        {member.age_group && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">Age {member.age_group}</span>}
                        {member.institution && <span className="rounded-full bg-purple-50 px-2 py-0.5 text-purple-700">{member.institution}</span>}
                        {member.referred_by && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">Referred by {member.referred_by}</span>}
                        {member.source === 'join_drive' && <span className="rounded-full bg-gray-900 px-2 py-0.5 text-white">/join drive</span>}
                        {member.payment_screenshot_url ? <a href={member.payment_screenshot_url} target="_blank" rel="noopener noreferrer" className="rounded-full bg-green-50 px-2 py-0.5 text-green-700 underline">Payment proof</a> : <span className="rounded-full bg-red-50 px-2 py-0.5 text-red-600">No payment proof</span>}
                      </div>
                    </div>
                    <Badge
                      className={
                        member.status === 'approved'
                          ? 'bg-party-green-100 text-party-green-700'
                          : member.status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }
                    >
                      {member.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Father's Name</p>
                      <p className="text-sm text-gray-600">{member.father_name}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Date of Birth</p>
                      <p className="text-sm text-gray-600">{member.dob}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">NID</p>
                      <p className="text-sm text-gray-600">{member.nid}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Mobile</p>
                      <p className="text-sm text-gray-600">{member.mobile}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Profession</p>
                      <p className="text-sm text-gray-600">{member.profession}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Education</p>
                      <p className="text-sm text-gray-600">{member.education}</p>
                    </div>
                  </div>
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-700">Address</p>
                    <p className="text-sm text-gray-600">{member.present_address}</p>
                  </div>
                  {member.status === 'pending' && (
                    <div className="flex space-x-2">
                      <Button
                        className="bg-party-green hover:bg-party-green-600"
                        onClick={() => updateMemberStatus(member._id, 'approved')}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => updateMemberStatus(member._id, 'rejected')}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {!loading && members.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No members found.
          </div>
        )}
      </main>
    </div>
  );
};

export default ManageMembers;